import { ApiError, errorFromResponse, NetworkError } from "./errors";

/** The app's single way to talk to the API (§17): JSON over /api with a Bearer token.
 *
 * - The transport is injectable: in mock mode the in-browser mock backend sits behind this
 *   exact interface, so error unwrapping and 401 handling are exercised the same way.
 * - A fetch that throws (the request never reached a server) becomes NetworkError; anything
 *   the server answered with a non-2xx status becomes ApiError carrying its `message`.
 * - Any 401 on an authenticated call clears the session (§16.5), except on endpoints where a
 *   401 means "those credentials are wrong", not "your session ended". */

export type Method = "GET" | "POST" | "PUT" | "DELETE";
export type Query = Record<string, string | number | boolean | null | undefined>;
export type ApiRequest = { method: Method; url: string; headers: Record<string, string>; body?: string; signal?: AbortSignal };
export type Transport = (req: ApiRequest) => Promise<Response>;
export type RequestOptions = {
  query?: Query;
  signal?: AbortSignal;
  /** Use this token instead of the session's (the impersonation handoff validates one before
   * adopting it). A 401 then never clears the current session. */
  token?: string;
};

export interface ApiClient {
  get<T>(path: string, opts?: RequestOptions): Promise<T>;
  post<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T>;
  put<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T>;
  del<T>(path: string, opts?: RequestOptions): Promise<T>;
}

/** A 401 from these means bad credentials in the form, never a dead session: a cashier whose
 * manager mistypes at checkout must not be signed out. */
const CREDENTIAL_PATHS = ["/auth/login", "/auth/authorize-override", "/auth/change-password", "/auth/reset-password", "/auth/forgot-password"];

export type ClientConfig = {
  transport?: Transport;
  baseUrl?: string;
  getToken?: () => string | null;
  onUnauthorized?: () => void;
};

export function buildUrl(base: string, path: string, query?: Query): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v === undefined || v === null || v === "") continue;
    qs.append(k, String(v));
  }
  const s = qs.toString();
  return `${base}${path}${s ? `?${s}` : ""}`;
}

const fetchTransport: Transport = (req) => fetch(req.url, { method: req.method, headers: req.headers, body: req.body, signal: req.signal });

export function createApiClient({ transport = fetchTransport, baseUrl = "/api", getToken = () => null, onUnauthorized }: ClientConfig = {}): ApiClient {
  async function request<T>(method: Method, path: string, body: unknown, opts: RequestOptions = {}): Promise<T> {
    const headers: Record<string, string> = { Accept: "application/json" };
    const explicit = opts.token !== undefined;
    const token = explicit ? opts.token! : getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    if (body !== undefined) headers["Content-Type"] = "application/json";

    let res: Response;
    try {
      res = await transport({ method, url: buildUrl(baseUrl, path, opts.query), headers, body: body === undefined ? undefined : JSON.stringify(body), signal: opts.signal });
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") throw e;
      throw new NetworkError();
    }

    if (!res.ok) {
      const err = await errorFromResponse(res);
      if (err.status === 401 && token && !explicit && !CREDENTIAL_PATHS.some((p) => path.startsWith(p))) onUnauthorized?.();
      throw err;
    }
    // 204 = "none" for /till/current and /till/last-closed (§17.1).
    if (res.status === 204) return null as T;
    const text = await res.text();
    if (!text) return null as T;
    try {
      return JSON.parse(text) as T;
    } catch {
      throw new ApiError(res.status, "The server sent a response the app could not read.");
    }
  }

  return {
    get: (path, opts) => request("GET", path, undefined, opts),
    post: (path, body, opts) => request("POST", path, body, opts),
    put: (path, body, opts) => request("PUT", path, body, opts),
    del: (path, opts) => request("DELETE", path, undefined, opts),
  };
}
